import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { IconButton, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import SydButton from '../../../../../../../commonDesign/Button'
import SydSelect from '../../../../../../../commonDesign/SydSelect'
import Switch from '../../../../../../../molecules/Switch'
import DragAndDropListItem from '../../../../../../../organisms/GroupingProvider/DragAndDropListItem'
import { formatDisplayName } from '../../../../../../../../utils/formatters'

const useStyles = makeStyles(() => ({
  grid: {
    fontSize: '0.875rem'
  },
  headerRow: {
    display: 'grid',
    gridTemplateColumns: '40px minmax(140px, 1fr) 1fr 1fr 100px 80px 60px 60px 60px 60px 50px',
    alignItems: 'center',
    backgroundColor: 'var(--color-neutral-700)',
    color: '#fff',
    fontWeight: 600,
    padding: '8px 0',
    gap: '4px',
    borderRadius: '4px 4px 0 0'
  },
  headerCell: {
    padding: '0 8px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  headerCellCenter: {
    padding: '0 8px',
    textAlign: 'center'
  },
  dataRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, 1fr) 1fr 1fr 100px 80px 60px 60px 60px 60px 50px',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 0',
    borderBottom: '1px solid var(--color-neutral-200)',
    width: '100%',
    '&:nth-child(even)': {
      backgroundColor: 'var(--color-neutral-50, #fafafa)'
    }
  },
  cell: {
    padding: '0 8px',
    overflow: 'hidden'
  },
  cellCenter: {
    padding: '0 8px',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  nameCell: {
    padding: '0 8px',
    fontWeight: 500,
    fontSize: '0.925rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  alignCell: {
    padding: '0 8px',
    overflow: 'hidden',
    '& select': {
      maxWidth: '100%'
    }
  },
  inputCell: {
    padding: '0 8px',
    '& input': {
      fontSize: '0.875rem',
      padding: '4px 8px'
    }
  },
  widthInput: {
    '& input': {
      fontSize: '0.875rem',
      padding: '4px 8px',
      textAlign: 'right'
    }
  },
  dragItem: {
    padding: '0 !important',
    '& > div:first-child': {
      marginRight: '0 !important'
    }
  },
  emptyRow: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    padding: '16px',
    textAlign: 'center',
    color: 'var(--text-color-subtle)',
    borderBottom: '1px solid var(--color-neutral-200)'
  }
}))

function ColumnConfigEditor ({
  columns,
  availableColumns,
  onAddColumn,
  onRemoveColumn,
  onUpdateColumn,
  onColumnsChange
}) {
  const classes = useStyles()
  const [columnToAdd, setColumnToAdd] = useState('')

  const selectableColumns = useMemo(() => {
    const existingNames = new Set(columns.map(c => c.name))
    return (availableColumns || [])
      .filter(pc => !existingNames.has(pc.name))
      .map(pc => ({
        label: `${pc.name} (${pc.dataType})`,
        value: pc.name
      }))
  }, [columns, availableColumns])

  const handleAddColumn = useCallback(() => {
    if (!columnToAdd) return

    const sourceColumn = availableColumns.find(c => c.name === columnToAdd)
    if (sourceColumn) {
      onAddColumn({
        name: sourceColumn.name,
        displayName: formatDisplayName(sourceColumn.name),
        description: sourceColumn.description || null,
        formatString: null,
        align: ['integer', 'decimal'].includes(sourceColumn.dataType) ? 'right' : 'left',
        sortable: sourceColumn.sortable !== false,
        filterable: sourceColumn.filterable !== false,
        hidden: sourceColumn.hidden || false,
        exclude: false,
        width: 150
      })
      setColumnToAdd('')
    }
  }, [columnToAdd, availableColumns, onAddColumn])

  const handleFieldChange = useCallback((index, field, value) => {
    const updated = { ...columns[index], [field]: value }
    onUpdateColumn(index, updated)
  }, [columns, onUpdateColumn])

  const onMoveItem = useCallback((dragIndex, hoverIndex) => {
    const next = [...columns]
    const dragged = next[dragIndex]
    next.splice(dragIndex, 1)
    next.splice(hoverIndex, 0, dragged)
    if (onColumnsChange) {
      onColumnsChange(next)
    }
  }, [columns, onColumnsChange])

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <h4 style={{ margin: 0, flex: 1 }}>Column Configuration ({columns.length})</h4>
      </div>

      <div className={classes.grid}>
        <div className={classes.headerRow}>
          <div className={classes.headerCell} />
          <div className={classes.headerCell}>Name</div>
          <div className={classes.headerCell}>Display Name</div>
          <div className={classes.headerCell}>Format</div>
          <div className={classes.headerCell}>Align</div>
          <div className={classes.headerCell}>Width</div>
          <div className={classes.headerCellCenter}>Sort</div>
          <div className={classes.headerCellCenter}>Filter</div>
          <div className={classes.headerCellCenter}>Hidden</div>
          <div className={classes.headerCellCenter}>Exclude</div>
          <div className={classes.headerCell} />
        </div>

        <DndProvider backend={HTML5Backend}>
          {columns.map((column, index) => (
            <DragAndDropListItem
              id={column.name}
              key={column.name}
              index={index}
              onMoveItem={onMoveItem}
              size='small'
              className={classes.dragItem}
            >
              <div className={classes.dataRow}>
                <div className={classes.nameCell} title={column.name}>
                  {column.name}
                </div>
                <div className={classes.inputCell}>
                  <TextField
                    value={column.displayName || ''}
                    onChange={(e) => handleFieldChange(index, 'displayName', e.target.value)}
                    size='small'
                    fullWidth
                    placeholder={column.name}
                  />
                </div>
                <div className={classes.inputCell}>
                  <TextField
                    value={column.formatString || ''}
                    onChange={(e) => handleFieldChange(index, 'formatString', e.target.value)}
                    size='small'
                    fullWidth
                    placeholder='e.g., currency, date'
                  />
                </div>
                <div className={classes.alignCell}>
                  <SydSelect
                    value={column.align || 'left'}
                    onChange={(e) => handleFieldChange(index, 'align', e.target.value)}
                    options={[
                      { label: 'Left', value: 'left' },
                      { label: 'Center', value: 'center' },
                      { label: 'Right', value: 'right' }
                    ]}
                    size='xs'
                    minWidth={0}
                  />
                </div>
                <div className={classes.inputCell}>
                  <TextField
                    value={column.width ?? 150}
                    onChange={(e) => handleFieldChange(index, 'width', parseInt(e.target.value, 10) || 150)}
                    size='small'
                    type='number'
                    className={classes.widthInput}
                    inputProps={{ min: 1, max: 4000, step: 10 }}
                    placeholder='150'
                  />
                </div>
                <div className={classes.cellCenter}>
                  <Switch
                    checked={column.sortable}
                    onChange={(e) => handleFieldChange(index, 'sortable', e.target.checked)}
                  />
                </div>
                <div className={classes.cellCenter}>
                  <Switch
                    checked={column.filterable}
                    onChange={(e) => handleFieldChange(index, 'filterable', e.target.checked)}
                  />
                </div>
                <div className={classes.cellCenter}>
                  <Switch
                    checked={column.hidden}
                    onChange={(e) => handleFieldChange(index, 'hidden', e.target.checked)}
                  />
                </div>
                <div className={classes.cellCenter}>
                  <Switch
                    checked={column.exclude}
                    onChange={(e) => handleFieldChange(index, 'exclude', e.target.checked)}
                  />
                </div>
                <div className={classes.cellCenter}>
                  <IconButton size='small' onClick={() => onRemoveColumn(index)}>
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </div>
              </div>
            </DragAndDropListItem>
          ))}
        </DndProvider>

        {columns.length === 0
          ? (
            <div className={classes.emptyRow}>
              No columns configured. Select a purpose to pre-fill columns, or add manually below.
            </div>
          )
          : null}
      </div>

      {selectableColumns.length > 0
        ? (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '16px' }}>
            <div style={{ flex: 1, maxWidth: '300px' }}>
              <SydSelect
                options={selectableColumns}
                value={columnToAdd}
                onChange={(e) => setColumnToAdd(e.target.value)}
                placeholder='Select column to add'
              />
            </div>
            <SydButton
              onClick={handleAddColumn}
              disabled={!columnToAdd}
              icon='add'
              size='sm'
            >
              Add Column
            </SydButton>
          </div>
        )
        : null}
    </div>
  )
}

ColumnConfigEditor.propTypes = {
  columns: PropTypes.array.isRequired,
  availableColumns: PropTypes.array,
  onAddColumn: PropTypes.func.isRequired,
  onRemoveColumn: PropTypes.func.isRequired,
  onUpdateColumn: PropTypes.func.isRequired,
  onColumnsChange: PropTypes.func
}

ColumnConfigEditor.defaultProps = {
  availableColumns: [],
  onColumnsChange: null
}

export default ColumnConfigEditor
